/**
 * "Rác còn sót" — OCR lines that still reach the AI after cleaning and repeat
 * across screenshots. Anh marks each one:
 *   🗑 Là rác          → stripped from the next Shortcut call (no deploy)
 *   ✓ Không phải rác   → real content (a shop reply, a customer line repeated
 *                        because the same chat was screenshotted twice) —
 *                        just hidden from this list
 * Rules match the WHOLE line only, never part of a sentence.
 */

import { useCallback, useEffect, useState } from 'react';
import { deleteNoiseRule, getNoiseLeftovers, getNoiseRules, setNoiseRule } from '../lib/api.js';
import type { NoiseLeftover, NoiseRule, NoiseVerdict } from '../types.js';

export function NoiseReviewSection({
  token,
  onOpenCall,
  onError,
}: {
  token: string | null;
  onOpenCall: (callId: string) => void;
  onError: (msg: string) => void;
}) {
  const [days, setDays] = useState(30);
  const [leftovers, setLeftovers] = useState<NoiseLeftover[] | null>(null);
  const [scanned, setScanned] = useState(0);
  const [rules, setRules] = useState<NoiseRule[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);

  const load = useCallback(() => {
    setLeftovers(null);
    Promise.all([getNoiseLeftovers(token, days), getNoiseRules(token)])
      .then(([l, r]) => {
        setLeftovers(l.leftovers);
        setScanned(l.scanned);
        setRules(r);
      })
      .catch((err: Error) => onError(err.message));
  }, [token, days, onError]);

  useEffect(load, [load]);

  async function mark(item: NoiseLeftover, verdict: NoiseVerdict) {
    setBusyKey(item.key);
    try {
      const rule = await setNoiseRule(token, item.line, verdict);
      // Instant feedback: drop the row, remember the rule — no full reload.
      setLeftovers((prev) => prev?.filter((x) => x.key !== item.key) ?? null);
      setRules((prev) => [rule, ...prev.filter((r) => r.key !== rule.key)]);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  }

  async function undo(rule: NoiseRule) {
    setBusyKey(rule.key);
    try {
      await deleteNoiseRule(token, rule.key);
      setRules((prev) => prev.filter((r) => r.key !== rule.key));
      load();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  }

  const noiseCount = rules.filter((r) => r.verdict === 'noise').length;

  return (
    <section className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">Rác còn sót</h2>
          <p className="text-xs text-muted mt-1">
            Dòng vẫn lọt tới AI sau khi lọc và lặp lại ở nhiều ảnh chụp
            {leftovers && ` · quét ${scanned.toLocaleString()} lần gọi`}. Đánh dấu “Là rác” → từ lần gọi sau
            Shortcut tự bỏ dòng đó (khớp nguyên dòng, không cần deploy).
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-card2 border border-border rounded-lg px-3 py-1.5 text-xs shrink-0"
        >
          <option value={7}>7 ngày</option>
          <option value={30}>30 ngày</option>
          <option value={90}>90 ngày</option>
        </select>
      </div>

      {!leftovers && <p className="px-5 py-6 text-sm text-muted">Đang quét…</p>}
      {leftovers?.length === 0 && (
        <p className="px-5 py-6 text-sm text-muted">Không còn dòng lặp nào chưa duyệt 🎉</p>
      )}

      {leftovers && leftovers.length > 0 && (
        <ul className="divide-y divide-border max-h-[480px] overflow-y-auto">
          {leftovers.map((item) => (
            <li key={item.key} className="px-5 py-2.5 flex items-center gap-3 text-sm">
              <span className="text-xs text-muted w-14 shrink-0 tabular-nums">{item.count} ảnh</span>
              <button
                onClick={() => onOpenCall(item.example_call_id)}
                title="Xem một ảnh có dòng này"
                className="flex-1 min-w-0 text-left font-mono text-xs truncate hover:text-accent"
              >
                {item.line}
              </button>
              <button
                disabled={busyKey === item.key}
                onClick={() => mark(item, 'noise')}
                className="text-xs border border-accent/50 text-accent hover:bg-accent/10 rounded-lg px-2.5 py-1 shrink-0 disabled:opacity-40"
              >
                🗑 Là rác
              </button>
              <button
                disabled={busyKey === item.key}
                onClick={() => mark(item, 'keep')}
                className="text-xs border border-border text-muted hover:text-white rounded-lg px-2.5 py-1 shrink-0 disabled:opacity-40"
              >
                ✓ Không phải rác
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="px-5 py-3 border-t border-border">
        <button onClick={() => setShowRules((v) => !v)} className="text-xs text-muted hover:text-white">
          {showRules ? '▾' : '▸'} Đã đánh dấu ({noiseCount} là rác · {rules.length - noiseCount} không phải rác)
        </button>
        {showRules && (
          <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
            {rules.length === 0 && <li className="text-xs text-muted">Chưa đánh dấu dòng nào.</li>}
            {rules.map((r) => (
              <li key={r.key} className="flex items-center gap-3 text-xs">
                <span className={r.verdict === 'noise' ? 'text-accent w-20 shrink-0' : 'text-muted w-20 shrink-0'}>
                  {r.verdict === 'noise' ? '🗑 rác' : '✓ giữ'}
                </span>
                <span className="flex-1 min-w-0 font-mono truncate">{r.line}</span>
                <button
                  disabled={busyKey === r.key}
                  onClick={() => undo(r)}
                  className="text-muted hover:text-white disabled:opacity-40"
                >
                  Huỷ
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * "AI nhận được" — every OCR line of one call; lines the cleaner drops are
 * struck through. A kept line can be marked as noise right here.
 */
export function AiSeesView({
  token,
  lines,
  onError,
}: {
  token: string | null;
  lines: { text: string; removed: boolean }[];
  onError: (msg: string) => void;
}) {
  const [marked, setMarked] = useState<Set<number>>(new Set());
  const removed = lines.filter((l) => l.removed).length;

  async function markNoise(i: number, text: string) {
    try {
      await setNoiseRule(token, text, 'noise');
      setMarked((prev) => new Set(prev).add(i));
    } catch (err) {
      onError((err as Error).message);
    }
  }

  return (
    <div>
      <div className="text-xs text-muted mb-1">
        AI nhận được <span className="opacity-70">· gạch ngang = đã lọc ({removed}/{lines.length} dòng)</span>
      </div>
      <div className="bg-card2 border border-border rounded-xl p-3 font-mono text-xs leading-relaxed">
        {lines.map((l, i) => {
          if (!l.text.trim()) return <div key={i} className="h-3" />;
          const gone = l.removed || marked.has(i);
          return (
            <div key={i} className="group flex items-start gap-2">
              <span className={gone ? 'flex-1 line-through text-muted/50' : 'flex-1 whitespace-pre-wrap'}>
                {l.text}
              </span>
              {!gone && (
                <button
                  onClick={() => markNoise(i, l.text)}
                  title="Đánh dấu dòng này là rác"
                  className="opacity-0 group-hover:opacity-100 text-muted hover:text-accent shrink-0"
                >
                  🗑
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
